import React, { useEffect, useState, useMemo } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TableContainer,
    Paper,
    Chip,
    CircularProgress,
    ToggleButtonGroup,
    ToggleButton,
    Tooltip,
    IconButton,
    TableSortLabel,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Divider,
    Alert,
    LinearProgress,
} from '@mui/material';
import {
    TrendingUp as TrendingUpIcon,
    AttachMoney as AttachMoneyIcon,
    Assessment as AssessmentIcon,
    ExpandMore as ExpandMoreIcon,
    Info as InfoIcon,
    Sort as SortIcon,
    FilterList as FilterIcon,
    BarChart as BarChartIcon,
    PieChart as PieChartIcon,
} from '@mui/icons-material';
import { PieChart } from '@mui/x-charts/PieChart';
import { LineChart } from '@mui/x-charts/LineChart';
import { BarChart } from '@mui/x-charts/BarChart';
import { useTranslation } from 'react-i18next';
import UserActivityService from '../../Services/userActivity.service';

const formatHours = (hours) => {
    if (hours === 0) return '-';
    const wholeHours = Math.floor(hours);
    const minutes = Math.round((hours - wholeHours) * 60);
    return `${wholeHours}:${minutes.toString().padStart(2, '0')}h`;
};

const formatCurrency = (amount) => {
    return `₪${amount.toFixed(2)}`;
};

export default function EfficiencyReportView({
    accountantId,
    filters,
    token,
    direction,
    subAccountants,
}) {
    const { t } = useTranslation();

    const [loading, setLoading] = useState(false);
    const [reportType, setReportType] = useState('employee'); // 'employee' or 'business'
    const [employeeData, setEmployeeData] = useState(null);
    const [businessData, setBusinessData] = useState(null);

    // Sorting and visualization state
    const [sortField, setSortField] = useState('');
    const [sortDirection, setSortDirection] = useState('asc');
    const [showVisualizations, setShowVisualizations] = useState(true);

    useEffect(() => {
        console.log('EfficiencyReportView: Filters changed, reloading reports', {
            startUtc: filters.startUtc,
            endUtc: filters.endUtc,
            reportType
        });
        loadCurrentReport();
    }, [accountantId, filters.startUtc, filters.endUtc, filters.subAccountantIds, filters.clientCaseIds, filters.activityType, token, reportType]);

    const loadCurrentReport = async () => {
        setLoading(true);
        try {
            if (reportType === 'employee') {
                await loadEmployeeReport();
            } else if (reportType === 'business') {
                await loadBusinessReport();
            }
        } catch (err) {
            console.error('Failed to load efficiency report', err);
        } finally {
            setLoading(false);
        }
    };

    const loadEmployeeReport = async () => {
        console.log('Loading Employee Efficiency Report with payload:', {
            AccountantId: accountantId,
            StartUtc: filters.startUtc.toISOString(),
            EndUtc: filters.endUtc.toISOString(),
            SubAccountantIds: filters.subAccountantIds?.map((sa) => sa.value) || [],
            CaseIds: filters.clientCaseIds?.map((c) => c.value) || [],
        });

        const employeePayload = {
            AccountantId: accountantId,
            StartUtc: filters.startUtc.toISOString(),
            EndUtc: filters.endUtc.toISOString(),
            SubAccountantIds: filters.subAccountantIds?.map((sa) => sa.value) || [],
            CaseIds: filters.clientCaseIds?.map((c) => c.value) || [],
            ActivityType: filters.activityType || null,
        };

        const employeeResponse = await UserActivityService.GetEmployeeEfficiencyReport(
            employeePayload,
            token
        );
        if (employeeResponse.ok) {
            const employeeJson = await employeeResponse.json();
            setEmployeeData(employeeJson);
        } else {
            console.error('Failed to load Employee Efficiency Report');
        }
    };

    const loadBusinessReport = async () => {
        console.log('Loading Business Efficiency Report with payload:', {
            AccountantId: accountantId,
            StartUtc: filters.startUtc.toISOString(),
            EndUtc: filters.endUtc.toISOString(),
            CaseIds: filters.clientCaseIds?.map((c) => c.value) || [],
        });

        const businessPayload = {
            AccountantId: accountantId,
            StartUtc: filters.startUtc.toISOString(),
            EndUtc: filters.endUtc.toISOString(),
            CaseIds: filters.clientCaseIds?.map((c) => c.value) || [],
            ActivityType: filters.activityType || null,
        };

        const businessResponse = await UserActivityService.GetBusinessEfficiencyReport(
            businessPayload,
            token
        );
        if (businessResponse.ok) {
            const businessJson = await businessResponse.json();
            setBusinessData(businessJson);
        } else {
            console.error('Failed to load Business Efficiency Report');
        }
    };

    const handleReportTypeChange = (event, newType) => {
        if (newType !== null) {
            setReportType(newType);
        }
    };

    // Filtering and sorting logic using FiltersBar filters
    const filteredAndSortedData = useMemo(() => {
        let data = reportType === 'employee' ? employeeData?.employees : businessData?.cases;
        if (!data) return [];

        if (reportType === 'employee' && filters.subAccountantIds?.length > 0) {
            const selectedIds = filters.subAccountantIds.map(sa => sa.value ?? sa);
            data = data.filter(emp => selectedIds.includes(emp.userId));
        }
        if (reportType === 'business' && filters.clientCaseIds?.length > 0) {
            const selectedCaseIds = filters.clientCaseIds.map(c => c.value ?? c);
            data = data.filter(caseItem => selectedCaseIds.includes(caseItem.caseId));
        }

        // Apply sorting
        if (sortField) {
            data = [...data].sort((a, b) => {
                let aValue = a[sortField];
                let bValue = b[sortField];

                if (typeof aValue === 'string') {
                    aValue = aValue.toLowerCase();
                    bValue = bValue.toLowerCase();
                }

                if (sortDirection === 'asc') {
                    return aValue > bValue ? 1 : -1;
                } else {
                    return aValue < bValue ? 1 : -1;
                }
            });
        }

        return data;
    }, [employeeData, businessData, reportType, filters.subAccountantIds, filters.clientCaseIds, sortField, sortDirection]);

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    // Enhanced chart data preparation with 2 decimal precision and null safety
    const chartData = useMemo(() => {
        if (reportType === 'employee') {
            const data = filteredAndSortedData.map(emp => {
                // Safe number formatting with null/undefined checks
                const safeNumber = (value, defaultValue = 0) => {
                    if (value === null || value === undefined || isNaN(value)) return defaultValue;
                    return parseFloat(Number(value).toFixed(2));
                };

                const attendanceHours = safeNumber(emp.totalAttendanceHours);
                const mouseHours = safeNumber(emp.totalMouseHours);
                const activities = emp.totalActivities || 0;
                const costAttendance = safeNumber(emp.totalCostFromAttendance);
                const costMouse = safeNumber(emp.totalCostFromMouse);
                const efficiencyAttendance = safeNumber(emp.efficiencyFromAttendance);
                const efficiencyMouse = safeNumber(emp.efficiencyFromMouse);

                return {
                    name: emp.name || 'Unknown',
                    attendanceHours,
                    mouseHours,
                    activities,
                    costAttendance,
                    costMouse,
                    efficiencyAttendance,
                    efficiencyMouse,
                    // Additional metrics for better analysis with minimum values for visibility
                    productivity: mouseHours > 0 ? safeNumber(activities / mouseHours) : safeNumber(activities * 0.1),
                    costPerHour: mouseHours > 0 ? safeNumber(costMouse / mouseHours) : safeNumber(costMouse * 0.1),
                    utilization: attendanceHours > 0 ? safeNumber((mouseHours / attendanceHours) * 100) : safeNumber(mouseHours * 10),
                    performance: efficiencyMouse > 0 ? safeNumber((1 / efficiencyMouse) * 100) : safeNumber(activities * 0.1),
                };
            });

            // Filter out employees with no meaningful data for charts
            return data.filter(emp => emp.activities > 0 || emp.mouseHours > 0 || emp.costMouse > 0);
        } else {
            const data = filteredAndSortedData.map(caseItem => {
                // Safe number formatting with null/undefined checks
                const safeNumber = (value, defaultValue = 0) => {
                    if (value === null || value === undefined || isNaN(value)) return defaultValue;
                    return parseFloat(Number(value).toFixed(2));
                };

                const mouseHours = safeNumber(caseItem.totalMouseHours);
                const manualHours = safeNumber(caseItem.totalManualHours);
                const totalHours = safeNumber(caseItem.totalHours ?? (mouseHours + manualHours));
                const activities = caseItem.totalActivities || 0;
                const cost = safeNumber(caseItem.totalCostFromTotal ?? caseItem.totalCostFromMouse);
                const efficiency = safeNumber(caseItem.efficiencyFromTotal ?? caseItem.efficiencyFromMouse);

                return {
                    name: caseItem.caseName || 'Unknown',
                    mouseHours,
                    manualHours,
                    totalHours,
                    activities,
                    cost,
                    efficiency,
                    // Additional metrics for case analysis with minimum values for visibility
                    productivity: totalHours > 0 ? safeNumber(activities / totalHours) : safeNumber(activities * 0.1),
                    costPerHour: totalHours > 0 ? safeNumber(cost / totalHours) : safeNumber(cost * 0.1),
                    complexity: totalHours > 0 ? safeNumber(activities / totalHours) : safeNumber(activities * 0.1),
                    roi: cost > 0 ? safeNumber(activities / cost) : safeNumber(activities * 0.1),
                };
            });

            // Filter out cases with no meaningful data for charts
            return data.filter(caseItem => caseItem.activities > 0 || caseItem.totalHours > 0 || caseItem.cost > 0);
        }
    }, [filteredAndSortedData, reportType]);

    if (loading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: 400,
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box dir={direction} sx={{ px: 2, mt: 2 }}>
            {/* Toggle between Employee and Business reports */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                <ToggleButtonGroup
                    value={reportType}
                    exclusive
                    onChange={handleReportTypeChange}
                    aria-label="report type"
                >
                    <ToggleButton value="employee" aria-label="employee report">
                        <TrendingUpIcon sx={{ mr: 1 }} />
                        {t('EmployeeEfficiency') || 'Employee Efficiency'}
                    </ToggleButton>
                    <ToggleButton value="business" aria-label="business report">
                        <AssessmentIcon sx={{ mr: 1 }} />
                        {t('BusinessEfficiency') || 'Business Efficiency'}
                    </ToggleButton>
                </ToggleButtonGroup>
            </Box>

            {/* Employee Efficiency Report */}
            {reportType === 'employee' && employeeData && (
                <Card
                    variant="outlined"
                    sx={{
                        mb: 4,
                        borderRadius: 3,
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
                        border: '1px solid rgba(0, 0, 0, 0.05)',
                        background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
                    }}
                >
                    <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography
                                variant="h6"
                                sx={{
                                    fontWeight: 700,
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    backgroundClip: 'text',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                }}
                            >
                                {t('EmployeeEfficiencyReport') || 'Employee Efficiency Report'}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    {showVisualizations ? t('HideVisualizations') : t('ShowVisualizations')}
                                </Typography>
                                <IconButton
                                    onClick={() => setShowVisualizations(!showVisualizations)}
                                    sx={{
                                        bgcolor: showVisualizations ? 'primary.main' : 'grey.300',
                                        color: showVisualizations ? 'white' : 'grey.600',
                                        '&:hover': {
                                            bgcolor: showVisualizations ? 'primary.dark' : 'grey.400'
                                        }
                                    }}
                                >
                                    {showVisualizations ? <BarChartIcon /> : <PieChartIcon />}
                                </IconButton>
                            </Box>
                        </Box>

                        {/* Enhanced Visualizations with Material-UI X-Charts */}
                        {showVisualizations && chartData && chartData.length > 0 && (
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <BarChartIcon color="primary" fontSize="small" />
                                    {t('AdvancedAnalytics') || 'Advanced Analytics'}
                                </Typography>
                                <Grid container spacing={2}>
                                    {/* Productivity vs Cost Analysis - Bar Chart */}
                                    <Grid item xs={8} md={6}>
                                        <Card sx={{ p: 1, height: 200 }}>
                                            <Typography variant="subtitle2" gutterBottom>{t('ProductivityVsCostAnalysis') || 'Productivity vs Cost Analysis'}</Typography>
                                            <BarChart
                                                dataset={chartData}
                                                xAxis={[{
                                                    scaleType: 'band',
                                                    dataKey: 'name',
                                                    label: t('Employee')
                                                }]}
                                                series={[
                                                    {
                                                        dataKey: 'productivity',
                                                        label: t('ProductivityActivitiesPerHour') || 'Productivity (activities/hr)',
                                                        color: '#3498db'
                                                    },
                                                    {
                                                        dataKey: 'costPerHour',
                                                        label: `${t('CostPerHour') || 'Cost per Hour'} (₪)`,
                                                        color: '#e74c3c'
                                                    }
                                                ]}

                                            />
                                        </Card>
                                    </Grid>

                                    {/* Performance Distribution - Pie Chart */}
                                    <Grid item xs={8} md={6}>
                                        <Card sx={{ p: 1, height: 200 }}>
                                            <Typography variant="subtitle2" gutterBottom>{t('PerformanceDistributionChart') || 'Performance Distribution Chart'}</Typography>
                                            <PieChart
                                                series={[{
                                                    data: chartData.slice(0, 5).map(item => ({
                                                        id: item.name,
                                                        value: item.performance,
                                                        label: item.name.substring(0, 10)
                                                    })),
                                                    highlightScope: { fade: 'global', highlight: 'item' },
                                                    faded: { innerRadius: 10, additionalRadius: -10, color: 'gray' },
                                                }]}
                                                height={150}
                                            />
                                        </Card>
                                    </Grid>
                                </Grid>
                            </Box>
                        )}

                        {/* Compact Explanation */}
                        <Accordion sx={{ mb: 2 }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <InfoIcon color="primary" fontSize="small" />
                                    <Typography variant="subtitle1">{t('HowThisReportWorks') || 'How This Report Works'}</Typography>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Grid container spacing={1}>
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <BarChartIcon color="primary" fontSize="small" />
                                            {t('DataSources') || 'Data Sources'}
                                        </Typography>
                                        <Alert severity="info" sx={{ mb: 1, py: 0.5 }}>
                                            <Typography variant="caption">
                                                <strong>{t('AttendanceHours')}:</strong> {t('AttendanceHoursExplanation') || 'Calculated from check-in/check-out pairs using the same logic as Monthly Schedule'}
                                            </Typography>
                                        </Alert>
                                        <Alert severity="info" sx={{ mb: 1, py: 0.5 }}>
                                            <Typography variant="caption">
                                                <strong>{t('MouseHours')}:</strong> {t('MouseHoursExplanation') || 'Actual work time based on mouse activity records (more accurate for productivity)'}
                                            </Typography>
                                        </Alert>
                                        <Alert severity="info" sx={{ py: 0.5 }}>
                                            <Typography variant="caption">
                                                <strong>{t('Activities')}:</strong> {t('ActivitiesExplanation') || 'Count of user activities performed during the selected period'}
                                            </Typography>
                                        </Alert>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <AttachMoneyIcon color="success" fontSize="small" />
                                            {t('CostCalculations') || 'Cost Calculations'}
                                        </Typography>
                                        <Alert severity="success" sx={{ mb: 1, py: 0.5 }}>
                                            <Typography variant="caption">
                                                <strong>{t('CostFromAttendance')}:</strong> {t('TotalCostExplanation') || 'Attendance Hours × Hourly Rate'}
                                            </Typography>
                                        </Alert>
                                        <Alert severity="success" sx={{ mb: 1, py: 0.5 }}>
                                            <Typography variant="caption">
                                                <strong>{t('CostFromMouse')}:</strong> {t('TotalCostExplanation') || 'Mouse Hours × Hourly Rate'}
                                            </Typography>
                                        </Alert>
                                        <Alert severity="success" sx={{ py: 0.5 }}>
                                            <Typography variant="caption">
                                                <strong>{t('Efficiency')}:</strong> {t('EfficiencyMetricExplanation') || 'Total Cost ÷ Total Activities (cost per activity)'}
                                            </Typography>
                                        </Alert>
                                    </Grid>
                                </Grid>
                            </AccordionDetails>
                        </Accordion>

                        <TableContainer
                            component={Paper}
                            sx={{
                                maxHeight: 600,
                                borderRadius: 2,
                                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06)',
                                border: '1px solid rgba(0, 0, 0, 0.05)',
                                overflow: 'auto',
                                direction: direction === 'rtl' ? 'rtl' : 'ltr',
                            }}
                        >
                            <Table stickyHeader size="small" dir={direction}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell
                                            sx={{
                                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                color: 'white',
                                                textAlign: 'center',
                                                fontWeight: 700,
                                                border: 'none',
                                            }}
                                        >
                                            <TableSortLabel
                                                active={sortField === 'name'}
                                                direction={sortField === 'name' ? sortDirection : 'asc'}
                                                onClick={() => handleSort('name')}
                                                sx={{ color: 'white' }}
                                            >
                                                {t('Employee') || 'Employee'}
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                color: 'white',
                                                textAlign: 'center',
                                                fontWeight: 700,
                                                border: 'none',
                                            }}
                                        >
                                            <TableSortLabel
                                                active={sortField === 'totalAttendanceHours'}
                                                direction={sortField === 'totalAttendanceHours' ? sortDirection : 'asc'}
                                                onClick={() => handleSort('totalAttendanceHours')}
                                                sx={{ color: 'white' }}
                                            >
                                                {t('AttendanceHours') || 'Attendance Hours'}
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                color: 'white',
                                                textAlign: 'center',
                                                fontWeight: 700,
                                                border: 'none',
                                            }}
                                        >
                                            <TableSortLabel
                                                active={sortField === 'totalMouseHours'}
                                                direction={sortField === 'totalMouseHours' ? sortDirection : 'asc'}
                                                onClick={() => handleSort('totalMouseHours')}
                                                sx={{ color: 'white' }}
                                            >
                                                {t('MouseHours') || 'Mouse Hours'}
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                color: 'white',
                                                textAlign: 'center',
                                                fontWeight: 700,
                                                border: 'none',
                                            }}
                                        >
                                            <TableSortLabel
                                                active={sortField === 'totalActivities'}
                                                direction={sortField === 'totalActivities' ? sortDirection : 'asc'}
                                                onClick={() => handleSort('totalActivities')}
                                                sx={{ color: 'white' }}
                                            >
                                                {t('Activities') || 'Activities'}
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                color: 'white',
                                                textAlign: 'center',
                                                fontWeight: 700,
                                                border: 'none',
                                            }}
                                        >
                                            {t('HourlyRate') || 'Hourly Rate'}
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                color: 'white',
                                                textAlign: 'center',
                                                fontWeight: 700,
                                                border: 'none',
                                            }}
                                        >
                                            {t('CostFromAttendance') || 'Cost (Attendance)'}
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                color: 'white',
                                                textAlign: 'center',
                                                fontWeight: 700,
                                                border: 'none',
                                            }}
                                        >
                                            {t('CostFromMouse') || 'Cost (Mouse)'}
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredAndSortedData.map((employee) => (
                                        <TableRow
                                            key={employee.userId}
                                            hover
                                            sx={{
                                                '&:hover': {
                                                    backgroundColor: 'rgba(102, 126, 234, 0.04)',
                                                },
                                            }}
                                        >
                                            <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>
                                                {employee.name}
                                            </TableCell>
                                            <TableCell sx={{ textAlign: 'center' }}>
                                                <Chip
                                                    label={formatHours(employee.totalAttendanceHours)}
                                                    color="primary"
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            </TableCell>
                                            <TableCell sx={{ textAlign: 'center' }}>
                                                <Chip
                                                    label={formatHours(employee.totalMouseHours)}
                                                    color="secondary"
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            </TableCell>
                                            <TableCell sx={{ textAlign: 'center' }}>
                                                {employee.totalActivities}
                                            </TableCell>
                                            <TableCell sx={{ textAlign: 'center' }}>
                                                {employee.hourlyRate
                                                    ? formatCurrency(employee.hourlyRate)
                                                    : '-'}
                                            </TableCell>
                                            <TableCell sx={{ textAlign: 'center', color: '#27ae60', fontWeight: 600 }}>
                                                {formatCurrency(employee.totalCostFromAttendance)}
                                            </TableCell>
                                            <TableCell sx={{ textAlign: 'center', color: '#e74c3c', fontWeight: 600 }}>
                                                {formatCurrency(employee.totalCostFromMouse)}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {/* Debug Information */}


                        {/* No Data Message */}
                        {showVisualizations && (!chartData || chartData.length === 0) && (
                            <Box sx={{ mt: 3, p: 3, textAlign: 'center', bgcolor: 'grey.50', borderRadius: 2 }}>
                                <Typography variant="h6" color="text.secondary" gutterBottom>
                                    {t('NoDataForVisualization') || 'No Data Available for Visualization'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {t('AdjustFiltersToSeeData') || 'Please adjust your date range or filters to see visualizations.'}
                                </Typography>
                            </Box>
                        )}

                    </CardContent>
                </Card>
            )}

            {/* Business Efficiency Report */}
            {reportType === 'business' && businessData && (
                <Card
                    variant="outlined"
                    sx={{
                        mb: 4,
                        borderRadius: 3,
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
                        border: '1px solid rgba(0, 0, 0, 0.05)',
                        background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
                    }}
                >
                    <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography
                                variant="h6"
                                sx={{
                                    fontWeight: 700,
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    backgroundClip: 'text',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                }}
                            >
                                {t('BusinessEfficiencyReport') || 'Business Efficiency Report'}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    {showVisualizations ? t('HideVisualizations') : t('ShowVisualizations')}
                                </Typography>
                                <IconButton
                                    onClick={() => setShowVisualizations(!showVisualizations)}
                                    sx={{
                                        bgcolor: showVisualizations ? 'primary.main' : 'grey.300',
                                        color: showVisualizations ? 'white' : 'grey.600',
                                        '&:hover': {
                                            bgcolor: showVisualizations ? 'primary.dark' : 'grey.400'
                                        }
                                    }}
                                >
                                    {showVisualizations ? <BarChartIcon /> : <PieChartIcon />}
                                </IconButton>
                            </Box>
                        </Box>

                        {/* Enhanced Business Visualizations with Material-UI X-Charts */}
                        {showVisualizations && chartData && chartData.length > 0 && (
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <BarChartIcon color="primary" fontSize="small" />
                                    {t('BusinessIntelligence') || 'Business Intelligence'}
                                </Typography>
                                <Grid container spacing={2}>
                                    {/* ROI vs Complexity Analysis - Line Chart */}
                                    <Grid item xs={8} md={6}>
                                        <Card sx={{ p: 1, height: 200 }}>
                                            <Typography variant="subtitle2" gutterBottom>{t('ROIVsComplexityAnalysis') || 'ROI vs Complexity Analysis'}</Typography>
                                            <LineChart
                                                dataset={chartData}
                                                xAxis={[{
                                                    scaleType: 'band',
                                                    dataKey: 'name',
                                                    label: t('Case') || 'Case'
                                                }]}
                                                series={[
                                                    {
                                                        dataKey: 'roi',
                                                        label: t('ROIActivitiesPerShekel') || 'ROI (activities/₪)',
                                                        color: '#4CAF50'
                                                    },
                                                    {
                                                        dataKey: 'complexity',
                                                        label: t('ComplexityActivitiesPerHour') || 'Complexity (activities/hr)',
                                                        color: '#FF9800'
                                                    }
                                                ]}
                                            />
                                        </Card>
                                    </Grid>

                                    {/* Case Cost Distribution - Pie Chart */}
                                    <Grid item xs={8} md={6}>
                                        <Card sx={{ p: 1, height: 200 }}>
                                            <Typography variant="subtitle2" gutterBottom>{t('CaseCostDistributionChart') || 'Case Cost Distribution Chart'}</Typography>
                                            <PieChart
                                                series={[{
                                                    data: chartData.slice(0, 5).map(item => ({
                                                        id: item.name,
                                                        value: item.cost,
                                                        label: item.name.substring(0, 10)
                                                    })),
                                                    highlightScope: { fade: 'global', highlight: 'item' },
                                                    faded: { innerRadius: 10, additionalRadius: -10, color: 'gray' },
                                                }]}
                                                height={150}

                                            />
                                        </Card>
                                    </Grid>
                                </Grid>
                            </Box>
                        )}

                        {/* Compact Business Explanation */}
                        <Accordion sx={{ mb: 2 }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <InfoIcon color="primary" fontSize="small" />
                                    <Typography variant="subtitle1">{t('BusinessEfficiencyAnalysis') || 'Business Efficiency Analysis'}</Typography>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Grid container spacing={1}>
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <TrendingUpIcon color="primary" fontSize="small" />
                                            {t('CasePerformance') || 'Case Performance'}
                                        </Typography>
                                        <Alert severity="info" sx={{ mb: 1, py: 0.5 }}>
                                            <Typography variant="caption">
                                                <strong>{t('MouseHours')}:</strong> {t('MouseHoursExplanation') || 'Actual work time spent on each case (tracked via mouse activity)'}
                                            </Typography>
                                        </Alert>
                                        <Alert severity="info" sx={{ mb: 1, py: 0.5 }}>
                                            <Typography variant="caption">
                                                <strong>{t('ManualHours') || 'Outside Xtram Hours'}:</strong> {t('ManualHoursExplanation') || 'Manually entered time spent on the client outside Xtram'}
                                            </Typography>
                                        </Alert>
                                        <Alert severity="info" sx={{ mb: 1, py: 0.5 }}>
                                            <Typography variant="caption">
                                                <strong>{t('TotalWorkHours') || 'Total Hours'}:</strong> {t('TotalWorkHoursExplanation') || 'System hours + outside-Xtram hours'}
                                            </Typography>
                                        </Alert>
                                        <Alert severity="info" sx={{ mb: 1, py: 0.5 }}>
                                            <Typography variant="caption">
                                                <strong>{t('Activities')}:</strong> {t('ActivitiesExplanation') || 'Number of actions performed on each case'}
                                            </Typography>
                                        </Alert>
                                        <Alert severity="info" sx={{ py: 0.5 }}>
                                            <Typography variant="caption">
                                                <strong>{t('Efficiency')}:</strong> {t('EfficiencyExplanation') || 'Cost per activity - lower is better'}
                                            </Typography>
                                        </Alert>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <AttachMoneyIcon color="success" fontSize="small" />
                                            {t('CostAnalysis') || 'Cost Analysis'}
                                        </Typography>
                                        <Alert severity="success" sx={{ mb: 1, py: 0.5 }}>
                                            <Typography variant="caption">
                                                <strong>{t('TotalCost')}:</strong> {t('TotalCostFromTotalExplanation') || 'Total Hours (system + outside) × Hourly Rate for all employees'}
                                            </Typography>
                                        </Alert>
                                        <Alert severity="success" sx={{ mb: 1, py: 0.5 }}>
                                            <Typography variant="caption">
                                                <strong>{t('EfficiencyMetric')}:</strong> {t('EfficiencyMetricExplanation') || 'Total Cost ÷ Total Activities'}
                                            </Typography>
                                        </Alert>
                                        <Alert severity="success" sx={{ py: 0.5 }}>
                                            <Typography variant="caption">
                                                <strong>{t('Goal')}:</strong> {t('GoalExplanation') || 'Minimize cost per activity for maximum efficiency'}
                                            </Typography>
                                        </Alert>
                                    </Grid>
                                </Grid>
                            </AccordionDetails>
                        </Accordion>

                        <TableContainer
                            component={Paper}
                            sx={{
                                maxHeight: 600,
                                borderRadius: 2,
                                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06)',
                                border: '1px solid rgba(0, 0, 0, 0.05)',
                                overflow: 'auto',
                                direction: direction === 'rtl' ? 'rtl' : 'ltr',
                            }}
                        >
                            <Table stickyHeader size="small" dir={direction}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell
                                            sx={{
                                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                color: 'white',
                                                textAlign: 'center',
                                                fontWeight: 700,
                                                border: 'none',
                                                cursor: 'pointer',
                                            }}
                                            onClick={() => handleSort('caseName')}
                                        >
                                            <TableSortLabel
                                                active={sortField === 'caseName'}
                                                direction={sortField === 'caseName' ? sortDirection : 'asc'}
                                                sx={{ color: 'white' }}
                                            >
                                                {t('Case') || 'Case'}
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                color: 'white',
                                                textAlign: 'center',
                                                fontWeight: 700,
                                                border: 'none',
                                                cursor: 'pointer',
                                            }}
                                            onClick={() => handleSort('totalMouseHours')}
                                        >
                                            <TableSortLabel
                                                active={sortField === 'totalMouseHours'}
                                                direction={sortField === 'totalMouseHours' ? sortDirection : 'asc'}
                                                sx={{ color: 'white' }}
                                            >
                                                {t('MouseHours') || 'Mouse Hours'}
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                color: 'white',
                                                textAlign: 'center',
                                                fontWeight: 700,
                                                border: 'none',
                                                cursor: 'pointer',
                                            }}
                                            onClick={() => handleSort('totalManualHours')}
                                        >
                                            <TableSortLabel
                                                active={sortField === 'totalManualHours'}
                                                direction={sortField === 'totalManualHours' ? sortDirection : 'asc'}
                                                sx={{ color: 'white' }}
                                            >
                                                {t('ManualHours') || 'Outside Xtram Hours'}
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                color: 'white',
                                                textAlign: 'center',
                                                fontWeight: 700,
                                                border: 'none',
                                                cursor: 'pointer',
                                            }}
                                            onClick={() => handleSort('totalHours')}
                                        >
                                            <TableSortLabel
                                                active={sortField === 'totalHours'}
                                                direction={sortField === 'totalHours' ? sortDirection : 'asc'}
                                                sx={{ color: 'white' }}
                                            >
                                                {t('TotalWorkHours') || 'Total Hours'}
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                color: 'white',
                                                textAlign: 'center',
                                                fontWeight: 700,
                                                border: 'none',
                                                cursor: 'pointer',
                                            }}
                                            onClick={() => handleSort('totalActivities')}
                                        >
                                            <TableSortLabel
                                                active={sortField === 'totalActivities'}
                                                direction={sortField === 'totalActivities' ? sortDirection : 'asc'}
                                                sx={{ color: 'white' }}
                                            >
                                                {t('Activities') || 'Activities'}
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                color: 'white',
                                                textAlign: 'center',
                                                fontWeight: 700,
                                                border: 'none',
                                                cursor: 'pointer',
                                            }}
                                            onClick={() => handleSort('totalCostFromTotal')}
                                        >
                                            <TableSortLabel
                                                active={sortField === 'totalCostFromTotal'}
                                                direction={sortField === 'totalCostFromTotal' ? sortDirection : 'asc'}
                                                sx={{ color: 'white' }}
                                            >
                                                {t('TotalCostMouse') || 'Total Cost'}
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                color: 'white',
                                                textAlign: 'center',
                                                fontWeight: 700,
                                                border: 'none',
                                                cursor: 'pointer',
                                            }}
                                            onClick={() => handleSort('efficiencyFromTotal')}
                                        >
                                            <TableSortLabel
                                                active={sortField === 'efficiencyFromTotal'}
                                                direction={sortField === 'efficiencyFromTotal' ? sortDirection : 'asc'}
                                                sx={{ color: 'white' }}
                                            >
                                                {t('EfficiencyMouse') || 'Efficiency'}
                                            </TableSortLabel>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredAndSortedData.map((caseData) => (
                                        <TableRow
                                            key={caseData.caseId || 'office'}
                                            hover
                                            sx={{
                                                '&:hover': {
                                                    backgroundColor: 'rgba(102, 126, 234, 0.04)',
                                                },
                                            }}
                                        >
                                            <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>
                                                {caseData.caseName}
                                            </TableCell>
                                            <TableCell sx={{ textAlign: 'center' }}>
                                                <Chip
                                                    label={formatHours(caseData.totalMouseHours)}
                                                    color="secondary"
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            </TableCell>
                                            <TableCell sx={{ textAlign: 'center' }}>
                                                <Chip
                                                    label={formatHours(caseData.totalManualHours || 0)}
                                                    color="warning"
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            </TableCell>
                                            <TableCell sx={{ textAlign: 'center' }}>
                                                <Chip
                                                    label={formatHours(caseData.totalHours ?? ((caseData.totalMouseHours || 0) + (caseData.totalManualHours || 0)))}
                                                    color="primary"
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell sx={{ textAlign: 'center' }}>
                                                {caseData.totalActivities}
                                            </TableCell>
                                            <TableCell sx={{ textAlign: 'center', color: '#e74c3c', fontWeight: 600 }}>
                                                {formatCurrency(caseData.totalCostFromTotal ?? caseData.totalCostFromMouse)}
                                            </TableCell>
                                            <TableCell sx={{ textAlign: 'center', color: '#9b59b6', fontWeight: 600 }}>
                                                {formatCurrency(caseData.efficiencyFromTotal ?? caseData.efficiencyFromMouse)}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {/* Enhanced Business Visualizations */}
                    </CardContent>
                </Card>
            )}
        </Box>
    );
}

